"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@/utils/cn";

/* ================= TYPES ================= */
type MotionState = "moving" | "idle";
type Direction = "left" | "right";

type ActionState =
  | "walk"
  | "stop-start"
  | "idle-loop"
  | "hover-start"
  | "hover-loop"
  | "hover-end";

/* ================= CONFIG ================= */
const AUTO_CLOSE_MS = 5000;
const WALK_FRAME_MS = 150;

/* ================= COMPONENT ================= */
export default function PopupSlider({ IMAGES }: { IMAGES: string[] }) {
  /* ---------- POPUP ---------- */
  const [open, setOpen] = useState(true);
  const [index, setIndex] = useState(0);
  const [isInteracted, setIsInteracted] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  /* ---------- MASCOT STATE ---------- */
  const [motionState, setMotionState] = useState<MotionState>("idle");
  const [actionState, setActionState] = useState<ActionState>("idle-loop");
  const [direction, setDirection] = useState<Direction>("right");
  const [frameIndex, setFrameIndex] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  /* ================= FRAMES ================= */
  const FRAMES = {
    walk: {
      left: [
        "/mascot/walk-left/walk-left-1.png",
        "/mascot/walk-left/walk-left-2.png",
        "/mascot/walk-left/walk-left-3.png",
        "/mascot/walk-left/walk-left-4.png",
        "/mascot/walk-left/walk-left-5.png",
        "/mascot/walk-left/walk-left-6.png",
        "/mascot/walk-left/walk-left-7.png",
        "/mascot/walk-left/walk-left-8.png",
        "/mascot/walk-left/walk-left-9.png",
        "/mascot/walk-left/walk-left-10.png",
      ],
      right: [
        "/mascot/walk-right/walk-right-1.png",
        "/mascot/walk-right/walk-right-2.png",
        "/mascot/walk-right/walk-right-3.png",
        "/mascot/walk-right/walk-right-4.png",
        "/mascot/walk-right/walk-right-5.png",
        "/mascot/walk-right/walk-right-6.png",
        "/mascot/walk-right/walk-right-7.png",
        "/mascot/walk-right/walk-right-8.png",
        "/mascot/walk-right/walk-right-9.png",
        "/mascot/walk-right/walk-right-10.png",
      ],
    },
    stop: {
      left: [
        "/mascot/left-stop/left-stop-3.png",
        "/mascot/left-stop/left-stop-2.png",
        "/mascot/left-stop/left-stop-1.png",
      ],
      right: [
        "/mascot/right-stop/right-stop-3.png",
        "/mascot/right-stop/right-stop-2.png",
        "/mascot/right-stop/right-stop-1.png",
      ],
    },
    idle: {
      left: [
        "/mascot/idle/idle-1.png",
        "/mascot/idle/idle-1.png",
        "/mascot/idle/idle-2.png",
        "/mascot/idle/idle-4.png",
      ],
      right: [
        "/mascot/idle/idle-1.png",
        "/mascot/idle/idle-1.png",
        "/mascot/idle/idle-2.png",
        "/mascot/idle/idle-4.png",
      ],
    },
    hover: {
      start: {
        left: [
          "/mascot/hover-start/hover-start-1.png",
          "/mascot/hover-start/hover-start-2.png",
          "/mascot/hover-start/hover-start-3.png",
        ],
        right: [
          "/mascot/hover-start/hover-start-1.png",
          "/mascot/hover-start/hover-start-2.png",
          "/mascot/hover-start/hover-start-3.png",
        ],
      },
      loop: {
        left: [
          "/mascot/hover-loop/hover-loop-1.png",
          "/mascot/hover-loop/hover-loop-2.png",
          "/mascot/hover-loop/hover-loop-3.png",
          "/mascot/hover-loop/hover-loop-4.png",
        ],
        right: [
          "/mascot/hover-loop/hover-loop-1.png",
          "/mascot/hover-loop/hover-loop-2.png",
          "/mascot/hover-loop/hover-loop-3.png",
          "/mascot/hover-loop/hover-loop-4.png",
        ],
      },
      end: {
        left: [
          "/mascot/hover-end/hover-end-1.png",
          "/mascot/hover-end/hover-end-2.png",
          "/mascot/hover-end/hover-end-3.png",
        ],
        right: [
          "/mascot/hover-end/hover-end-1.png",
          "/mascot/hover-end/hover-end-2.png",
          "/mascot/hover-end/hover-end-3.png",
        ],
      },
    },
  } as const;
  const getStopStartIndexFromWalk = (walkIndex: number) => {
    // walk 0–9 → stop 0–2
    if (walkIndex <= 3) return 0;
    if (walkIndex <= 6) return 1;
    return 2;
  };

  useEffect(() => {
    const preloadFrames = [
      ...FRAMES.idle.left,
      ...FRAMES.idle.right,
      ...FRAMES.walk.left,
      ...FRAMES.walk.right,
    ];

    preloadFrames.forEach((src) => {
      const img = document.createElement("img");
      img.src = src;
    });
  }, []);

  /* ================= POPUP AUTO CLOSE ================= */
  useEffect(() => {
    closeTimerRef.current = setTimeout(() => {
      if (!isInteracted) setOpen(false);
    }, AUTO_CLOSE_MS);

    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isInteracted]);

  const markInteracted = () => {
    if (!isInteracted) {
      setIsInteracted(true);
      closeTimerRef.current && clearTimeout(closeTimerRef.current);
    }
  };

  const next = () => {
    markInteracted();
    setIndex((prev) => (prev + 1) % IMAGES.length);
  };

  const prev = () => {
    markInteracted();
    setIndex((prev) => (prev === 0 ? IMAGES.length - 1 : prev - 1));
  };

  const scrollToContactAndClose = () => {
    setOpen(false);

    requestAnimationFrame(() => {
      const el = document.getElementById("contact");
      if (!el) return;

      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };
  /* ================= SCROLL → MOTION ================= */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;

      if (y !== lastScrollY.current) {
        setMotionState("moving");
        setActionState("walk");
        setDirection(y > lastScrollY.current ? "right" : "left");
        lastScrollY.current = y;

        scrollTimeout.current && clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
          setMotionState("idle");
          setActionState("stop-start");
          setFrameIndex((i) => getStopStartIndexFromWalk(i));
        }, 120);
      }

      /* translate X */
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const progress = Math.min(Math.max(y / docHeight, 0), 1);
        setScrollX(progress * (window.innerWidth - 180));
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  /* ================= ANIMATION ENGINE ================= */
  useEffect(() => {
    let frames: readonly string[] = [];
    let loop = false;
    let speed = WALK_FRAME_MS;

    switch (actionState) {
      case "walk":
        frames = FRAMES.walk[direction];
        loop = true;
        break;

      case "stop-start":
        frames = FRAMES.stop[direction];
        speed = 120;
        break;

      case "idle-loop":
        frames = FRAMES.idle[direction];
        speed = 400;
        loop = true;
        break;

      case "hover-start":
        frames = FRAMES.hover.start[direction];
        speed = 100;
        break;

      case "hover-loop":
        frames = FRAMES.hover.loop[direction];
        speed = 250;
        loop = true;
        break;

      case "hover-end":
        frames = FRAMES.hover.end[direction];
        speed = 100;
        break;
    }

    setFrameIndex(0);

    const interval = setInterval(() => {
      setFrameIndex((i) => {
        if (i === frames.length - 1) {
          if (!loop) {
            if (actionState === "stop-start") setActionState("idle-loop");
            if (actionState === "hover-start") setActionState("hover-loop");
            if (actionState === "hover-end") setActionState("idle-loop");
            return i;
          }
          return 0;
        }
        return i + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [actionState, direction]);

  /* ================= HOVER (IDLE ONLY) ================= */
  const onHoverStart = () => {
    if (motionState === "idle") setActionState("hover-start");
  };
  const onHoverEnd = () => {
    if (motionState === "idle") setActionState("hover-end");
  };

  const currentFrame = actionState.startsWith("hover")
    ? FRAMES.hover[
        actionState === "hover-loop"
          ? "loop"
          : actionState === "hover-start"
            ? "start"
            : "end"
      ][direction][frameIndex]
    : FRAMES[
        actionState === "walk"
          ? "walk"
          : actionState === "stop-start"
            ? "stop"
            : "idle"
      ][direction][frameIndex];

  /* ================= RENDER ================= */
  return (
    <>
      {/* POPUP */}
      {open && (
        <div className="fixed inset-0 z-modal flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            onMouseEnter={markInteracted}
            onClick={markInteracted}
            className="relative z-10 w-[90%] md:w-[70%] lg:w-1/2 animate-popup-in"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 z-20 text-white bg-black/50 rounded-full w-9 h-9"
            >
              ✕
            </button>

            <div className="relative aspect-[12/9] overflow-hidden">
              <div
                className="flex h-full transition-transform duration-500"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {IMAGES.map((src, i) => (
                  <div key={i} className="relative w-full flex-shrink-0">
                    <Image
                      src={src}
                      alt=""
                      fill
                      quality={80}
                      priority={i === 0}
                      loading={i === 0 ? "eager" : "lazy"}
                      sizes="(min-width: 1024px) 50vw, (min-width: 768px) 70vw, 90vw"
                      className="object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
            {/* Controls */}
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white w-10 h-10 rounded-full hover:bg-black"
            >
              ‹
            </button>

            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 text-white w-10 h-10 rounded-full hover:bg-black"
            >
              ›
            </button>

            {/* Footer actions */}
            <div className="flex items-center justify-center gap-6 px-6 py-4 ">
              <button
                type="button"
                onClick={scrollToContactAndClose}
                className="
                  px-6 py-2
                  text-sm font-semibold tracking-wide
                  text-white
                  bg-primary hover:bg-primaryDark
                 
                  transition
                  shadow-md hover:shadow-lg
                "
              >
                LIÊN HỆ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MASCOT */}
      {!open && (
        <button
          onMouseEnter={onHoverStart}
          onMouseLeave={onHoverEnd}
          onClick={() => setOpen(true)}
          className="hidden md:flex fixed bottom-2 left-2 z-modal w-36 h-36    group"
          style={{ transform: `translateX(${scrollX}px)` }}
        >
          <div
            className={cn(motionState === "moving" && "animate-mascot-walk")}
          >
            <Image
              src={currentFrame}
              alt="Mascot"
              width={130}
              height={130}
              className="
                pointer-events-none select-none
                transition-all duration-300
                group-hover:scale-105
                group-hover:drop-shadow-[0_10px_18px_rgba(255,245,180,0.9)]
              "
            />
          </div>
        </button>
      )}
    </>
  );
}
